import {
    Node, SurveySession, SurveySessionNodeProgress, SurveySessionProgressSummary, SurveySessionProgressTree
} from "@/types/models";
import {createContext, PropsWithChildren, useContext, useEffect, useState} from "react";
import {getProgressDetail, getProgressSummary, getProgressTree} from "@/services/NodeService";
import {useSessionMetadataContext} from "@/Context/SessionMetadataContext";

type ProgressContextProps = {
    loading: boolean
    setLoading: (loading: boolean) => void
    activeNodeIsVisible: boolean
    setActiveNodeIsVisible: (set: boolean) => void
    forceShowActiveNode: boolean
    setForceShowActiveNode: (set: boolean) => void
    progressSummary: SurveySessionProgressSummary | undefined
    setProgressSummary: (summary: SurveySessionProgressSummary | undefined) => void
    fetchProgressSummary: () => Promise<SurveySessionProgressSummary | undefined>
    /** Progress for each row of the contents tree. It is fetched whenever the summary is. */
    progressTree: SurveySessionProgressTree | undefined
    progressDetail: SurveySessionNodeProgress | undefined
    setProgressDetail: (detail: SurveySessionNodeProgress | undefined) => void
    fetchProgressDetail: (node: Node | undefined) => Promise<SurveySessionNodeProgress | undefined>
}

const ProgressContext = createContext<ProgressContextProps | undefined>(undefined)

export const ProgressContextProvider = ({children}: PropsWithChildren) => {
    const {session} = useSessionMetadataContext()
    const [loading, setLoading] = useState(false)
    const [activeNodeIsVisible, setActiveNodeIsVisible] = useState(true)
    const [forceShowActiveNode, setForceShowActiveNode] = useState(true)
    const [progressSummary, setProgressSummary] = useState<SurveySessionProgressSummary | undefined>(undefined)
    const [progressTree, setProgressTree] = useState<SurveySessionProgressTree | undefined>(undefined)
    const [progressDetail, setProgressDetail] = useState<SurveySessionNodeProgress | undefined>(undefined)

    const fetchProgressSummary = async () => {
        if (!session) return undefined

        // The tree shows the same answers a row at a time. It can do without, so a failure is left at that.
        getProgressTree(session).then(setProgressTree).catch(() => {})

        const promise = getProgressSummary(session)
        promise.then((progress: SurveySessionProgressSummary) => {
            setProgressSummary(progress)
        })

        return promise
    }

    const fetchProgressDetail = async (node?: Node) => {
        if (!session) return undefined
        const promise = getProgressDetail(session, node)
        promise.then((progress: SurveySessionNodeProgress) => {
            setProgressDetail(progress)
        })

        return promise
    }

    useEffect(() => {
        setForceShowActiveNode(false)
    }, [activeNodeIsVisible])

    useEffect(() => {
        if (session) {
            fetchProgressSummary()
        }
    }, [session])

    return <ProgressContext.Provider value={{
        loading,
        setLoading,
        activeNodeIsVisible,
        setActiveNodeIsVisible,
        forceShowActiveNode,
        setForceShowActiveNode,
        progressSummary,
        setProgressSummary,
        fetchProgressSummary,
        progressTree,
        progressDetail,
        setProgressDetail,
        fetchProgressDetail,
    }}>
        {children}
    </ProgressContext.Provider>
}

export function useProgressContext(): ProgressContextProps {
    const context = useContext(ProgressContext);
    if (!context) throw new Error("No ProgressContext is defined")
    return context
}
